<template>
  <Teleport to="body">
    <Transition name="upload-fade">
      <div v-if="open" class="upload-overlay" @mousedown.self="!loading && $emit('cancel')">
        <section ref="cardRef" class="upload-card" role="dialog" aria-modal="true" :aria-labelledby="titleId" tabindex="-1" @keydown="onKeydown">
          <header class="upload-head">
            <div class="upload-icon"><Upload :size="21" /></div>
            <div>
              <h3 :id="titleId">{{ title }}</h3>
              <p>{{ loading ? (loadingMessage || (isPdf ? '正在进行 PDF 分层识别并上传，请稍候…' : '正在上传模版，请稍候…')) : description }}</p>
            </div>
            <button v-if="!loading" type="button" class="upload-close" aria-label="关闭" @click="$emit('cancel')">×</button>
          </header>

          <div v-if="loading && $slots.loading" class="upload-loading-body">
            <slot name="loading"></slot>
          </div>

          <div v-else-if="loading" class="upload-loading-body">
            <div v-if="hasSelectedFiles" class="upload-processing-badge">
              <span class="processing-dot"></span>
              <span class="processing-name">{{ selectedFileLabel }}</span>
              <span class="processing-size">{{ formatFileSize(selectedTotalSize) }}</span>
            </div>
            <WhaleLoading
              :fullscreen="false"
              :custom-tip="loadingMessage || (isPdf ? '正在进行 PDF 矢量分层解析与模版导入，请稍候…' : '正在导入模版，请稍候…')"
            />
          </div>

          <div v-else class="upload-body">
            <label v-if="!isBatchSelection" class="upload-field" :class="{ 'has-error': errorMessage }">
              <span>{{ nameLabel }}</span>
              <input
                ref="nameInputRef"
                :value="nameValue"
                type="text"
                maxlength="60"
                :placeholder="namePlaceholder"
                :aria-invalid="errorMessage ? 'true' : 'false'"
                @input="$emit('update:nameValue', $event.target.value)"
              />
              <p v-if="errorMessage" class="upload-error" role="alert">{{ errorMessage }}</p>
            </label>

            <label v-if="categoryOptions.length" class="upload-field">
              <span>素材类型</span>
              <select
                :value="categoryValue"
                @change="$emit('update:categoryValue', $event.target.value)"
              >
                <option v-for="option in categoryOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </label>

            <div class="upload-field">
              <span>选择文件</span>
              <button type="button" class="file-picker" @click="fileInputRef?.click()">
                <span class="file-picker-icon"><FolderOpen :size="19" /></span>
                <span class="file-picker-copy">
                  <strong>{{ selectedFileLabel || '点击打开文件夹' }}</strong>
                  <small>{{ hasSelectedFiles ? selectedFileHint : fileHint }}</small>
                </span>
                <span class="file-picker-action">{{ hasSelectedFiles ? '重新选择' : '浏览' }}</span>
              </button>
              <input
                ref="fileInputRef"
                class="hidden-file"
                type="file"
                :accept="accept"
                :multiple="multiple"
                @change="onFileChange"
              />
              <p v-if="isBatchSelection" class="batch-file-note">将使用每个文件的原始文件名作为素材名称</p>
              <p v-if="isBatchSelection && errorMessage" class="upload-error" role="alert">{{ errorMessage }}</p>
            </div>
          </div>

          <footer class="upload-actions">
            <button type="button" class="upload-btn" :disabled="loading" @click="$emit('cancel')">取消</button>
            <button
              type="button"
              class="upload-btn primary"
              :disabled="!hasSelectedFiles || (!isBatchSelection && !nameValue.trim()) || loading"
              @click="$emit('confirm')"
            >
              <span v-if="loading" class="btn-spinner" aria-hidden="true"></span>
              <span>{{ loading ? (loadingText || '正在导入…') : confirmText }}</span>
            </button>
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { FolderOpen, Upload } from '@lucide/vue';
import WhaleLoading from '@/components/common/WhaleLoading.vue';

const props = defineProps({
  open: Boolean,
  title: { type: String, default: '上传文件' },
  description: { type: String, default: '填写名称并选择需要上传的文件。' },
  nameLabel: { type: String, default: '名称' },
  namePlaceholder: { type: String, default: '请输入名称' },
  nameValue: { type: String, default: '' },
  file: { type: Object, default: null },
  files: { type: Array, default: () => [] },
  multiple: Boolean,
  categoryValue: { type: String, default: '' },
  categoryOptions: { type: Array, default: () => [] },
  accept: { type: String, default: '' },
  fileHint: { type: String, default: '请选择本机文件' },
  confirmText: { type: String, default: '确认上传' },
  loadingText: { type: String, default: '上传中…' },
  loading: Boolean,
  loadingMessage: { type: String, default: '' },
  isPdf: Boolean,
  errorMessage: { type: String, default: '' },
});

const emit = defineEmits(['cancel', 'confirm', 'update:nameValue', 'update:file', 'update:files', 'update:categoryValue']);
const fileInputRef = ref(null);
const nameInputRef = ref(null);
const cardRef = ref(null);
const titleId = `upload-dialog-${Math.random().toString(36).slice(2, 8)}`;
let previousFocus = null;

const selectedFiles = computed(() => {
  if (props.files.length) return props.files.filter(Boolean);
  return props.file ? [props.file] : [];
});
const hasSelectedFiles = computed(() => selectedFiles.value.length > 0);
const isBatchSelection = computed(() => props.multiple && selectedFiles.value.length > 1);
const selectedTotalSize = computed(() => selectedFiles.value.reduce((total, file) => total + (Number(file?.size) || 0), 0));
const selectedFileLabel = computed(() => {
  if (!selectedFiles.value.length) return '';
  if (isBatchSelection.value) return `已选择 ${selectedFiles.value.length} 个文件`;
  return selectedFiles.value[0]?.name || '';
});
const selectedFileHint = computed(() => {
  if (!selectedFiles.value.length) return props.fileHint;
  if (isBatchSelection.value) return `总大小 ${formatFileSize(selectedTotalSize.value)}`;
  return formatFileSize(selectedFiles.value[0]?.size);
});

watch(() => props.open, async (open) => {
  if (!open) {
    previousFocus?.focus?.();
    return;
  }
  previousFocus = document.activeElement;
  await nextTick();
  nameInputRef.value?.focus();
});

function onKeydown(event) {
  if (event.key === 'Escape' && !props.loading) {
    event.preventDefault();
    emit('cancel');
    return;
  }
  if (event.key !== 'Tab') return;
  const focusable = [...cardRef.value.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled)')];
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function onFileChange(event) {
  const files = Array.from(event.target.files || []);
  const selected = props.multiple ? files : files.slice(0, 1);
  const file = selected[0] || null;
  event.target.value = '';
  emit('update:files', selected);
  emit('update:file', file);
  if (selected.length === 1 && file && !props.nameValue.trim()) {
    emit('update:nameValue', file.name.replace(/\.[^.]+$/, ''));
  }
}

function formatFileSize(size) {
  const bytes = Number(size) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
</script>

<style scoped>
.upload-overlay {
  position: fixed; inset: 0; z-index: 12000;
  display: grid; place-items: center; padding: 20px;
  background: rgba(15, 23, 42, .46); backdrop-filter: blur(3px);
}
.upload-card {
  width: min(500px, 100%); overflow: hidden;
  border: 1px solid rgba(226, 232, 240, .9); border-radius: 18px;
  background: #fff; box-shadow: 0 28px 80px rgba(15, 23, 42, .24);
}
.upload-head {
  position: relative; display: flex; align-items: center; gap: 12px;
  padding: 22px 24px 18px; border-bottom: 1px solid #eef2f7;
}
.upload-icon {
  width: 42px; height: 42px; flex: 0 0 auto;
  display: grid; place-items: center; border-radius: 12px;
  color: var(--color-info-text); background: var(--color-info-light); border: 1px solid var(--color-info-border);
}
.upload-head h3 { margin: 0; color: #0f172a; font-size: 18px; font-weight: 750; }
.upload-head p { margin: 4px 36px 0 0; color: #64748b; font-size: 12px; line-height: 1.5; }
.upload-close {
  position: absolute; top: 16px; right: 16px;
  width: 32px; height: 32px; border: 0; border-radius: 9px;
  background: #f8fafc; color: #64748b; font-size: 20px; cursor: pointer;
}
.upload-body { display: grid; gap: 18px; padding: 22px 24px 24px; }
.upload-loading-body {
  padding: 24px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}
.upload-processing-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 90%;
  padding: 6px 14px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  font-size: 12px;
  color: #334155;
}
.processing-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.2);
  animation: pulse-dot 1.6s ease-in-out infinite;
}
@keyframes pulse-dot {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.3); opacity: 0.6; }
}
.processing-name {
  font-weight: 600;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.processing-size {
  color: #64748b;
  font-size: 11px;
}
.btn-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  margin-right: 6px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.upload-field { display: grid; gap: 7px; }
.upload-field > span { color: #475569; font-size: 12px; font-weight: 650; }
.upload-field > input,
.upload-field > select {
  width: 100%; height: 42px; box-sizing: border-box;
  padding: 0 12px; border: 1px solid #dbe3ed; border-radius: 10px;
  outline: none; color: #0f172a; font-size: 14px;
}
.upload-field > input:focus,
.upload-field > select:focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, .12); }
.upload-field.has-error > input {
  border-color: #f87171;
  background: #fffafb;
}
.upload-field.has-error > input:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.14);
}
.upload-error {
  margin: 0;
  color: #dc2626;
  font-size: 12px;
  line-height: 1.5;
}
.batch-file-note {
  margin: 0;
  color: #64748b;
  font-size: 11px;
  line-height: 1.5;
}
.file-picker {
  width: 100%; min-height: 72px; display: flex; align-items: center; gap: 12px;
  padding: 12px; border: 1px dashed #cbd5e1; border-radius: 12px;
  background: #f8fafc; text-align: left; cursor: pointer;
}
.file-picker:hover { border-color: #7c3aed; background: #f5f3ff; }
.file-picker-icon {
  width: 40px; height: 40px; display: grid; place-items: center; flex: 0 0 auto;
  border-radius: 10px; color: #7c3aed; background: #fff; border: 1px solid #dbe3ed;
}
.file-picker-copy { display: grid; gap: 3px; min-width: 0; flex: 1; }
.file-picker-copy strong {
  overflow: hidden; color: #1e293b; font-size: 13px;
  text-overflow: ellipsis; white-space: nowrap;
}
.file-picker-copy small { color: #94a3b8; font-size: 11px; }
.file-picker-action { color: #7c3aed; font-size: 12px; font-weight: 650; }
.hidden-file { position: absolute; width: 0; height: 0; opacity: 0; pointer-events: none; }
.upload-actions {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 24px 18px; border-top: 1px solid #eef2f7; background: #fbfdff;
}
.upload-btn {
  min-width: 88px; height: 38px; padding: 0 17px;
  border: 1px solid #dbe3ed; border-radius: 999px;
  color: #334155; background: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
}
.upload-btn.primary { border-color: #7c3aed; color: #fff; background: #7c3aed; }
.upload-btn.primary:hover { background: #0b665f; }
.upload-btn:disabled { opacity: .45; cursor: not-allowed; }
.upload-fade-enter-active, .upload-fade-leave-active { transition: opacity .18s ease; }
.upload-fade-enter-from, .upload-fade-leave-to { opacity: 0; }
</style>
